import { picklist as vPicklist } from 'valibot'; // destruct to be sure that we don't ship whole valibot library to frontend
import type { InferOutput } from 'valibot';

import type { DynamoPropertyRadius } from '@@/server/property/international-diffusion/dynamo/validator';
import type { Location, LocationSearchResult } from '@@/shared/location/types';
import type { EpcGes } from '@@/shared/property/epc-ges';
import type { PropertySearchCriteria } from '@@/shared/property/search-criteria.ts';
import type { Language } from '@@/shared/application';
import type { ServerTranslationFunction } from '@@/server/translations';
import type { IadAppRoute } from '~/application';

// TRANSACTION TYPE

export type TransactionType = InferOutput<typeof TransactionTypeSchema>;
export const TransactionTypeSchema = vPicklist([
  `sale`,
  `rental`,
]);
export const TRANSACTION_TYPE = {
  SALE: `sale`,
  RENTAL: `rental`,
} satisfies Record<Uppercase<TransactionType>, TransactionType>;
export const TRANSACTION_TYPE_VALUES = Object.values(TRANSACTION_TYPE);

// PROPERTY TYPE

export type PropertyType = InferOutput<typeof PropertyTypeSchema>;
export const PropertyTypeSchema = vPicklist([
  `apartment`,
  `house`,
  `parking`,
  `land`,
  `building`,
  `business-asset`,
  `commercial-walls`,
  `lease-hold`,
]);

export type WebsitePropertySubType = typeof WEBSITE_PROPERTY_SUB_TYPES[keyof typeof WEBSITE_PROPERTY_SUB_TYPES];
export const WEBSITE_PROPERTY_SUB_TYPES = {
  // Use what should be available in international broadcast
  // https://github.com/IAD-INTERNATIONAL/playiad-serverless-diffusion/blob/main/src/validators/propertyListing.ts
  // https://github.com/IAD-INTERNATIONAL/playiad-serverless-diffusion/blob/ADV-2787-handle-event-create-property/src/validators/propertyListing.ts
  // Lines below are what is existing but that we don't want to keep for us (aka “makes no sense”)
  /*
    • DIVERSE: 'diverse',
    • DIVERSE_SURFACE: 'diverse-surface',
    • OTHER_SURFACE: 'other-surface',
  */
  _NONE: '',
  APARTMENT: 'apartment',
  ARCHITECT_HOUSE: 'architect-house',
  BARN: 'barn',
  BARGE: 'barge',
  BASTIDE: 'bastide',
  BOAT_RING: 'boat-ring',
  BUILDING: 'building',
  BUNGALOW: 'bungalow',
  CASTLE: 'castle',
  COTTAGE: 'cottage',
  COUNTRY_HOUSE: 'country-house',
  DOMAIN: 'domain',
  DUPLEX: 'duplex',
  FARMLAND: 'farmland',
  FARMHOUSE: 'farmhouse',
  GARAGE: 'garage',
  HOME: 'home',
  HOTEL: 'hotel',
  LAND: 'land',
  LAND_TO_BUILD: 'land-to-build',
  LOFT: 'loft',
  LONGERE: 'longere',
  LODGE: 'lodge',
  MANOR: 'manor',
  MANSION: 'mansion',
  MAS: 'mas',
  PARKING: 'parking',
  PRIVATE_HOTEL: 'private-hotel',
  RECREATION_GROUND: 'recreation-ground',
  SOUPLEX: 'souplex',
  STUDIO: 'studio',
  STUD_FARM: 'stud-farm',
  TOWN_HOUSE: 'town-house',
  TRADITIONAL_HOUSE: 'traditional-house',
  TRIPLEX: 'triplex',
  VILLAGE_HOUSE: 'village-house',
  WINDMILL: 'windmill',
  WORKSHOP: 'workshop',
  // Those are types in Playiad Vue
  BUSINESS_ASSET: `business-asset`,
  COMMERCIAL_WALLS: 'commercial-walls',
  LEASEHOLD: `lease-hold`,
  // Those are business extracted from Playiad's Discover squad
  BAR: 'bar',
  BAR_BRASSERIE: 'bar-brasserie',
  BAR_TOBACCO_SHOP: 'bar-tobacco-shop',
  BAKERY: 'bakery',
  BREWERY: 'brewery',
  SHOP_OR_COMMERCIAL_PREMISES: 'shop-or-commercial-premises',
  BRASSERIE: 'brasserie',
  OFFICES: 'offices',
  CAMPSITE: 'campsite',
  CIVET_SHOP: 'civet-shop',
  CREPERIE: 'creperie',
  HANGAR: 'hangar',
  TWO_STAR_HOTEL: 'two-star-hotel',
  THREE_STAR_HOTEL: 'three-star-hotel',
  FOUR_STAR_HOTEL: 'four-star-hotel',
  FIVE_STAR_HOTEL: 'five-star-hotel',
  PREFECTURAL_HOTEL: 'prefectural-hotel',
  HOTEL_RESTAURANT: 'hotel-restaurant',
  BUSINESS_PREMISES: 'business-premises',
  PIZZERIA: 'pizzeria',
  RESTAURANT: 'restaurant',
  GOURMET_RESTAURANT: 'gourmet-restaurant',
  FAST_FOOD_RESTAURANT: 'fast-food-restaurant',
  TEAROOM: 'tearoom',
  SANDWICH_SHOP: 'sandwich-shop',
  TOBACCO_SHOP: 'tobacco-shop',
} as const;

// Those are ordered as expected in display
// See for product rules: https://iad-holding.atlassian.net/browse/WEB-311
export type WebsiteTag = typeof WEBSITE_TAGS[keyof typeof WEBSITE_TAGS];
export type PropertyDisplayType = WebsitePropertySubType | PropertyType;
export const WEBSITE_TAGS = {
  NEWLY_PUBLISHED: 'newly-published',
  NEW_BUILD: 'new-build',
  PRESTIGE: 'prestige',
} as const;

export interface PropertySalePricesSeller {
  type: typeof TRANSACTION_TYPE.SALE
  main: number
  perSurface: number
  feesCharge: 'seller'
}
export interface PropertySalePricesBuyer {
  type: typeof TRANSACTION_TYPE.SALE
  main: number
  perSurface: number
  netPrice: number
  fees: number
  feesRatio: number
  feesCharge: 'buyer'
}
export type PropertySalePrices = PropertySalePricesSeller | PropertySalePricesBuyer;

export interface PropertyRentalPrices {
  type: typeof TRANSACTION_TYPE.RENTAL
  main: number
  leaseholderFees?: number
  securityDeposit?: number
}
export type PropertyPrices = PropertySalePrices | PropertyRentalPrices;

export interface PropertyOnCard {
  propertyListingRef: string
  description: string
  agentLocalId: number
  location: Location
  isExclusive: boolean
  isContractSigned: boolean
  photos: Array<string>
  videos?: Array<string>
  virtualTours?: Array<string>
  prices: Pick<PropertyPrices, 'main'>
  tags: Array<WebsiteTag>
  transactionType: TransactionType
  propertyType: PropertyType
  propertyDisplayType: PropertyDisplayType
  // Type langues as string as “locale.value” coming from useI18n() isn't typed'
  slugs: Record<string, string>
  rooms: Array<PropertyRoom>
  surfaces: Array<PropertySurface>
  transactionBuyerAgentLocalId?: number
  transactionPaidAt?: string
  transactionBuyerAgent?: {
    to: IadAppRoute<`real-estate-agent-userName`>
    text: string
  }
}

export interface LocationNoMap extends Location {
  hasMapInfo: false
};
export interface LocationWithMap extends Location {
  hasMapInfo: true
  latitude: number
  longitude: number
  radius?: DynamoPropertyRadius
  displayPin: boolean
};
export type LocationWithMapInfo = LocationNoMap | LocationWithMap;
export interface Property extends PropertyOnCard {
  prices: PropertyPrices
  location: LocationWithMapInfo
  epcGes: EpcGes
  descriptionSeo: string
  roomsCount: number
  surface?: number
  hasFeatures: boolean
  mainFeatures: Array<MainFeature>
  secondaryFeatures: Array<SecondaryFeature>
  hasCoOwnership: boolean
  coOwnership: {
    proceedingsUnderway?: boolean
    unitsCount?: number
    annualFees?: number
  }
}

type PropertyRoomType = 'rooms' | 'bedrooms';
type PropertyRoomIcon = 'iad-icon:bedrooms-count' | 'iad-icon:multi-rooms';
interface PropertyRoom {
  type: PropertyRoomType
  value: number
  icon: PropertyRoomIcon
};

// Those are the rules for surfaces in Playiad
// https://iad-holding.atlassian.net/browse/DIS-1599
export type PropertySurfaceType = 'living-area' | 'plot-area' | 'gross-area' | 'constructible-area';
export type PropertySurfaceIcon = 'iad-icon:living-area' | 'iad-icon:nature';
interface PropertySurface {
  type: PropertySurfaceType
  value: number
  icon: PropertySurfaceIcon
};

export type WebsiteMainFeature = typeof WEBSITE_MAIN_FEATURES[keyof typeof WEBSITE_MAIN_FEATURES];
export const WEBSITE_MAIN_FEATURES = {
  BATHROOM: 'bathroom',
  WASHROOM: 'washroom',
  TOILET: 'toilet',
  GARAGE: 'garage',
  BOX: 'box',
  TERRACE: 'terrace',
  BALCONY: 'balcony',
  POOL: 'pool',
  CELLAR: 'cellar',
  ELEVATOR: 'elevator',
  GARDEN: 'garden',
  AIR_CONDITIONER: 'air-conditioner',
  ALARM: 'alarm',
  FIREPLACE: 'fireplace',
} as const;

export type WebsiteSecondaryFeature = typeof WEBSITE_SECONDARY_FEATURES[keyof typeof WEBSITE_SECONDARY_FEATURES];
export const WEBSITE_SECONDARY_FEATURES = {
  KITCHEN_AMERICAN: 'kitchen-american',
  KITCHEN_SEPARATED: 'kitchen-separated',
  KITCHEN_CORNER: 'kitchen-corner',
  KITCHEN_EQUIPPED: 'kitchen-equipped',
  KITCHEN_EQUIPPED_AMERICAN: 'kitchen-equipped-american',
  KITCHEN_EQUIPPED_SEPARATED: 'kitchen-equipped-separated',
  KITCHEN_EQUIPPED_CORNER: 'kitchen-equipped-corner',
  FLOORS_HOUSE: 'floors-house',
  FLOORS_BUILDING: 'floors-building',
  FLOORS_APARTMENT: 'floors-apartment',
  FURNISHED: 'furnished',
  CUPBOARD: 'cupboard',
  TV: 'tv',
  HEATING: 'heating',
  HEATING_INDIVIDUAL: 'heating-individual',
  HEATING_COLLECTIVE: 'heating-collective',
  HEATING_ELECTRIC: 'heating-electric',
  HEATING_GAS: 'heating-gas',
  HEATING_FUEL: 'heating-fuel',
  CONSTRUCTION_YEAR: 'construction-year',
  DIGITAL_CODE: 'digital-code',
  INTERCOM: 'intercom',
  CARETAKER: 'caretaker',
  JOINT_OWNERSHIP_SIDE: 'joint-ownership-side',
  LIVING_ROOM: 'living-room',
  DINING_ROOM: 'dining-room',
} as const;

export interface MainFeature {
  type: WebsiteMainFeature
  value: number
  icon: 'iad-icon:bathtub' | 'iad-icon:shower' | 'iad-icon:toilet' | 'iad-icon:garage' | 'iad-icon:deck' | 'iad-icon:balcony' | 'iad-icon:pool' | 'iad-icon:shelves' | 'iad-icon:elevator' | 'iad-icon:mode-cool' | 'iad-icon:detector-alarm' | 'iad-icon:fireplace' | 'iad-icon:nature'
}

export interface SecondaryFeature {
  type: WebsiteSecondaryFeature
  value: number
  secondaryValue?: number
  translationKey?: Array<string>
}

// https://iad-holding.atlassian.net/browse/WEB-509
export type FilterableFeature = typeof FILTERABLE_FEATURES[number];
export const FILTERABLE_FEATURES = [
  WEBSITE_MAIN_FEATURES.BALCONY,
  WEBSITE_MAIN_FEATURES.TERRACE,
  WEBSITE_MAIN_FEATURES.GARAGE,
  WEBSITE_MAIN_FEATURES.CELLAR,
  WEBSITE_MAIN_FEATURES.ELEVATOR,
  WEBSITE_MAIN_FEATURES.POOL,
  // NOTE: garden/furnished deactivated until data is available
  // WEBSITE_MAIN_FEATURES.GARDEN,
  // WEBSITE_SECONDARY_FEATURES.FURNISHED,
] as const;
export const FilterableFeatureSchema = vPicklist(FILTERABLE_FEATURES);

// https://iad-holding.atlassian.net/browse/WEB-490
export const PROJECT_TYPES = {
  USED: `used`,
  NEW: `new`,
  // PRESTIGE: `prestige`,
  // LIFE_ANNUITY: `life-annuity`,
} as const;
export const PROJECT_TYPES_VALUES = Object.values(PROJECT_TYPES);
export const ProjectTypeSchema = vPicklist(PROJECT_TYPES_VALUES);
export type ProjectType = typeof PROJECT_TYPES[keyof typeof PROJECT_TYPES];

export interface SerpPageMetaParams {
  propertySearchCriteria: PropertySearchCriteria
  locations: Array<LocationSearchResult>
  totalItems?: number
}

export interface SerpPageParams extends SerpPageMetaParams {
  t: ServerTranslationFunction
  locale: Language
}
